import React, { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import type {
  GHAEmailClassification,
  GHAEmailDomainRule,
  UpsertGHAEmailDomainRuleBody,
} from '~/services/ConnectionGoogleService';

import { GHA_EMAIL_CLASSIFICATION_OPTIONS } from './constants';
import { isValidEmailDomain, normaliseEmailDomain } from './utils';

interface Props {
  open: boolean;
  /** Present = edit that domain; the domain is the key, so it cannot change. */
  initialRule?: GHAEmailDomainRule | null;
  isSaving: boolean;
  submitError?: string | null;
  onClose: () => void;
  onSubmit: (domain: string, input: UpsertGHAEmailDomainRuleBody) => void;
}

interface FormState {
  domain: string;
  classification: GHAEmailClassification;
  reason: string;
}

const toFormState = (rule: GHAEmailDomainRule | null | undefined): FormState => ({
  domain: rule?.domain ?? '',
  classification: rule?.classification ?? 'hotel',
  reason: rule?.reason ?? '',
});

export default function EmailDomainRuleDialog({ open, initialRule, isSaving, submitError, onClose, onSubmit }: Props) {
  const [form, setForm] = useState<FormState>(() => toFormState(initialRule));
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(toFormState(initialRule));
      setValidationError(null);
    }
  }, [open, initialRule]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = () => {
    const domain = normaliseEmailDomain(form.domain);
    if (!isValidEmailDomain(domain)) {
      setValidationError('Enter a domain such as hyatt.com, not an email address');
      return;
    }
    setValidationError(null);
    const reason = form.reason.trim();
    onSubmit(domain, { classification: form.classification, reason: reason === '' ? undefined : reason });
  };

  const isEdit = initialRule != null;
  const error = validationError ?? submitError;

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit email domain rule' : 'New email domain rule'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            size="small"
            label="Domain"
            placeholder="hyatt.com"
            value={form.domain}
            disabled={isEdit}
            onChange={(event) => set('domain', event.target.value)}
            helperText="Covers every address at this domain and its direct subdomains (mail.hyatt.com)."
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="email-domain-rule-classification">Classification</InputLabel>
            <Select
              labelId="email-domain-rule-classification"
              label="Classification"
              value={form.classification}
              onChange={(event) => set('classification', event.target.value)}
            >
              {GHA_EMAIL_CLASSIFICATION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Reason (optional)"
            placeholder="Hotel group domain"
            value={form.reason}
            onChange={(event) => set('reason', event.target.value)}
            inputProps={{ maxLength: 500 }}
            fullWidth
          />
          {error != null && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
          {isEdit ? 'Save' : 'Create rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
